import { useEffect, useState, useMemo, useCallback } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Typography,
  Box,
  IconButton,
  Snackbar,
  Grid,
  Button,
  ThemeProvider,
  CssBaseline,
  Tooltip,
  Select,
  Option,
  Chip,
} from '@mui/joy';
import {
  Delete as DeleteIcon,
  Edit,
  KeyboardArrowRight,
  Add,
  Visibility,
} from '@mui/icons-material';
import Breadcrumbs from '@mui/joy/Breadcrumbs';
import Link from '@mui/joy/Link';
import { DataGrid } from '@mui/x-data-grid';
import { useNavigate, useLocation } from 'react-router-dom';
import { listPricingPlans, deletePricingPlan } from '../../main/API/pricingPlan';
import { listPricingRules } from '../../main/API/pricingRule';
import CustomTextField from '../../main/widgets/CustomTextField';
import DeleteAlert from '../../services/services_manager/views/widgets/DeleteAlert';
import { theme } from '../../main/theme/constants';
import PermissionGate from '../../main/components/PermissionGate';
import { PERMISSIONS } from '../../main/context/AuthContext';
import { usePermissions } from '../../main/hooks/usePermissions';
import { masterApiErrorMessage } from '../../main/utils/masterApiError';
import { dataGridNativeCheckboxSlots } from '../../main/components/dataGridNativeCheckboxSlots';

/** v6: `valueGetter(params)` only. v7+: `(value, row, column, apiRef)`. */
function valueGetterRow(arg1, arg2) {
  if (arg2 !== undefined) return arg2;
  return arg1?.row;
}

export default function PricingPlansManagerScreen() {
  const { t } = useTranslation('ui');
  const { hasPermission } = usePermissions();
  const navigate = useNavigate();
  const location = useLocation();

  const [data, setData] = useState(null);
  const [results, setResults] = useState([]);
  const [deleteAlert, setDeleteAlert] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [successDelete, setSuccessDelete] = useState(false);
  const [failedDelete, setFailedDelete] = useState(false);
  const [deleteErrorMsg, setDeleteErrorMsg] = useState('');
  const [deleteRuleCount, setDeleteRuleCount] = useState(null);
  const [deleteConfirmLoading, setDeleteConfirmLoading] = useState(false);
  const [deletedRuleCount, setDeletedRuleCount] = useState(null);
  const [showCreatedSnack, setShowCreatedSnack] = useState(false);
  const [searchField, setSearchField] = useState('');
  const [pricingRuleSearch, setPricingRuleSearch] = useState('');
  const [activeFilter, setActiveFilter] = useState('');
  const [menuFilter, setMenuFilter] = useState('');
  // Dedicated field filters, sent only when the user fills them in.
  const [codeFilter, setCodeFilter] = useState('');
  const [nameFilter, setNameFilter] = useState('');
  const [nameArFilter, setNameArFilter] = useState('');
  const [descriptionFilter, setDescriptionFilter] = useState('');
  const [descriptionArFilter, setDescriptionArFilter] = useState('');
  const [menuLabelEnFilter, setMenuLabelEnFilter] = useState('');
  const [menuLabelArFilter, setMenuLabelArFilter] = useState('');
  const [menuOrderFilter, setMenuOrderFilter] = useState('');
  const [createdAfter, setCreatedAfter] = useState('');
  const [createdBefore, setCreatedBefore] = useState('');
  const [modifiedAfter, setModifiedAfter] = useState('');
  const [modifiedBefore, setModifiedBefore] = useState('');
  const [selectedRow, setSelectedRow] = useState(null);
  const [selectionModel, setSelectionModel] = useState([]);
  const [bulkDeleteAlert, setBulkDeleteAlert] = useState(false);
  const [bulkDeleteLoading, setBulkDeleteLoading] = useState(false);
  const [bulkDeleteConfirmLoading, setBulkDeleteConfirmLoading] = useState(false);
  const [bulkDeleteRuleCount, setBulkDeleteRuleCount] = useState(null);
  const [bulkDeletedPlanCount, setBulkDeletedPlanCount] = useState(null);
  const [bulkDeletedRuleCount, setBulkDeletedRuleCount] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 50,
  });

  const fetchData = useCallback(
    async (isSearch = false) => {
      if (!hasPermission(PERMISSIONS.PRICING_PLANS_LIST)) {
        setResults([]);
        setData(null);
        return;
      }
      if (isSearch) {
        setPaginationModel((prev) => ({ ...prev, page: 0 }));
      }
      setIsLoading(true);
      try {
        const page = isSearch ? 1 : paginationModel.page + 1;
        const params = {
          page,
          page_size: paginationModel.pageSize,
          ordering: 'menu_order',
        };
        if (searchField.trim()) params.search = searchField.trim();
        if (pricingRuleSearch.trim()) {
          params.pricing_rule_search = pricingRuleSearch.trim();
        }
        if (activeFilter === 'true' || activeFilter === 'false') {
          params.active = activeFilter === 'true';
        }
        if (menuFilter === 'true' || menuFilter === 'false') {
          params.show_in_menu = menuFilter === 'true';
        }
        // Text contains filters match the plan fields one by one.
        if (codeFilter.trim()) params.code = codeFilter.trim();
        if (nameFilter.trim()) params.name = nameFilter.trim();
        if (nameArFilter.trim()) params.name_ar = nameArFilter.trim();
        if (descriptionFilter.trim()) params.description = descriptionFilter.trim();
        if (descriptionArFilter.trim()) params.description_ar = descriptionArFilter.trim();
        if (menuLabelEnFilter.trim()) params.menu_label_en = menuLabelEnFilter.trim();
        if (menuLabelArFilter.trim()) params.menu_label_ar = menuLabelArFilter.trim();
        // Exact menu order and inclusive created/modified calendar days.
        if (menuOrderFilter.trim()) params.menu_order = menuOrderFilter.trim();
        if (createdAfter) params.created_at_after = createdAfter;
        if (createdBefore) params.created_at_before = createdBefore;
        if (modifiedAfter) params.modified_at_after = modifiedAfter;
        if (modifiedBefore) params.modified_at_before = modifiedBefore;
        const response = await listPricingPlans(params);
        const actualData = response.data;
        setData(actualData);
        const list = Array.isArray(actualData)
          ? actualData
          : Array.isArray(actualData?.results)
            ? actualData.results
            : [];
        setResults(list);
      } catch (err) {
        console.error(err.message);
        setResults([]);
        setData(null);
      } finally {
        setIsLoading(false);
      }
    },
    [
      hasPermission,
      searchField,
      pricingRuleSearch,
      activeFilter,
      menuFilter,
      codeFilter,
      nameFilter,
      nameArFilter,
      descriptionFilter,
      descriptionArFilter,
      menuLabelEnFilter,
      menuLabelArFilter,
      menuOrderFilter,
      createdAfter,
      createdBefore,
      modifiedAfter,
      modifiedBefore,
      paginationModel.page,
      paginationModel.pageSize,
    ]
  );

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginationModel.page, paginationModel.pageSize]);

  const handleSubmitFilter = async (event) => {
    event.preventDefault();
    await fetchData(true);
  };

  const deleteConfirmBody = useMemo(() => {
    if (deleteRuleCount === 0) return t('pricingPlans.deleteConfirmBodyNoRules');
    if (deleteRuleCount != null && deleteRuleCount > 0) {
      return t('pricingPlans.deleteConfirmBodyWithRules', { count: deleteRuleCount });
    }
    return t('pricingPlans.deleteConfirmBody');
  }, [deleteRuleCount, t]);

  const bulkDeleteConfirmBody = useMemo(() => {
    const planCount = selectionModel.length;
    if (planCount === 0) return t('pricingPlans.bulkDeleteConfirmBody');
    if (bulkDeleteRuleCount === 0) {
      return t('pricingPlans.bulkDeleteConfirmBodyNoRules', { planCount });
    }
    if (bulkDeleteRuleCount != null && bulkDeleteRuleCount > 0) {
      return t('pricingPlans.bulkDeleteConfirmBodyWithRules', {
        planCount,
        ruleCount: bulkDeleteRuleCount,
      });
    }
    return t('pricingPlans.bulkDeleteConfirmBody', { planCount });
  }, [bulkDeleteRuleCount, selectionModel.length, t]);

  const bulkDeleteSuccessMessage = useMemo(() => {
    if (bulkDeletedPlanCount != null && bulkDeletedPlanCount > 1) {
      if (bulkDeletedRuleCount != null && bulkDeletedRuleCount > 0) {
        return t('pricingPlans.bulkDeletedSuccessWithRules', {
          planCount: bulkDeletedPlanCount,
          ruleCount: bulkDeletedRuleCount,
        });
      }
      return t('pricingPlans.bulkDeletedSuccess', { planCount: bulkDeletedPlanCount });
    }
    if (deletedRuleCount != null && deletedRuleCount > 0) {
      return t('pricingPlans.deletedSuccessWithRules', { count: deletedRuleCount });
    }
    return t('pricingPlans.deletedSuccess');
  }, [bulkDeletedPlanCount, bulkDeletedRuleCount, deletedRuleCount, t]);

  const openDeleteDialog = async (row) => {
    setSelectedRow(row);
    setDeleteRuleCount(null);
    setDeleteAlert(true);
    setDeleteConfirmLoading(true);
    try {
      const response = await listPricingRules({ plan: row.id, page: 1, page_size: 1 });
      const data = response.data;
      const count =
        data?.count != null
          ? data.count
          : Array.isArray(data)
            ? data.length
            : Array.isArray(data?.results)
              ? data.results.length
              : null;
      setDeleteRuleCount(count);
    } catch (err) {
      console.error(err);
      setDeleteRuleCount(null);
    } finally {
      setDeleteConfirmLoading(false);
    }
  };

  const openBulkDeleteDialog = async () => {
    if (!selectionModel.length) return;
    setBulkDeleteRuleCount(null);
    setBulkDeleteAlert(true);
    setBulkDeleteConfirmLoading(true);
    try {
      const counts = await Promise.all(
        selectionModel.map((planId) =>
          listPricingRules({ plan: planId, page: 1, page_size: 1 })
            .then((response) => {
              const data = response.data;
              if (data?.count != null) return data.count;
              if (Array.isArray(data)) return data.length;
              if (Array.isArray(data?.results)) return data.results.length;
              return 0;
            })
            .catch(() => 0)
        )
      );
      setBulkDeleteRuleCount(counts.reduce((sum, count) => sum + count, 0));
    } catch (err) {
      console.error(err);
      setBulkDeleteRuleCount(null);
    } finally {
      setBulkDeleteConfirmLoading(false);
    }
  };

  const bulkDeletePlans = async () => {
    if (!hasPermission(PERMISSIONS.PRICING_PLANS_DELETE) || !selectionModel.length) return;
    setBulkDeleteLoading(true);
    const idsToDelete = [...selectionModel];
    try {
      await Promise.all(idsToDelete.map((id) => deletePricingPlan(id)));
      const deletedIds = new Set(idsToDelete);
      setResults((prev) => prev.filter((item) => !deletedIds.has(item.id)));
      setBulkDeletedPlanCount(idsToDelete.length);
      setBulkDeletedRuleCount(bulkDeleteRuleCount);
      setDeletedRuleCount(null);
      setSuccessDelete(true);
      setSelectionModel([]);
    } catch (e) {
      setFailedDelete(true);
      setDeleteErrorMsg(masterApiErrorMessage(e, t) || e.message);
    }
    setBulkDeleteLoading(false);
    setBulkDeleteAlert(false);
  };

  const deleteRow = async (row) => {
    if (!hasPermission(PERMISSIONS.PRICING_PLANS_DELETE)) return;
    setDeleteLoading(true);
    try {
      const response = await deletePricingPlan(row.id);
      if (response.status === 200 || response.status === 204) {
        setResults((prev) => prev.filter((item) => item.id !== row.id));
        setBulkDeletedPlanCount(null);
        setDeletedRuleCount(deleteRuleCount);
        setSuccessDelete(true);
      } else {
        setFailedDelete(true);
        setDeleteErrorMsg(
          t('pricingPlans.deleteFailedDetail', {
            detail: response.data?.detail ? ` ${response.data.detail}` : '',
          })
        );
      }
    } catch (e) {
      setFailedDelete(true);
      setDeleteErrorMsg(masterApiErrorMessage(e, t) || e.message);
    }
    setDeleteLoading(false);
    setDeleteAlert(false);
  };

  const justCreated = Boolean(location.state && location.state.pricingPlanJustCreated);
  const justDeleted = Boolean(location.state && location.state.pricingPlanJustDeleted);

  useEffect(() => {
    if (!justCreated) return;
    setShowCreatedSnack(true);
    fetchData(true);
    navigate(
      { pathname: location.pathname, search: location.search, hash: location.hash },
      { replace: true, state: {} }
    );
  }, [
    justCreated,
    fetchData,
    navigate,
    location.pathname,
    location.search,
    location.hash,
  ]);

  useEffect(() => {
    if (!justDeleted) return;
    setDeletedRuleCount(
      Number.isFinite(location.state?.deletedRuleCount)
        ? location.state.deletedRuleCount
        : null
    );
    setBulkDeletedPlanCount(null);
    setSuccessDelete(true);
    fetchData(true);
    navigate(
      { pathname: location.pathname, search: location.search, hash: location.hash },
      { replace: true, state: {} }
    );
  }, [
    justDeleted,
    fetchData,
    navigate,
    location.pathname,
    location.search,
    location.hash,
    location.state,
  ]);

  const columns = useMemo(
    () => [
      { field: 'id', headerName: t('trackingEvent.colId'), width: 70 },
      { field: 'code', headerName: t('pricingPlans.colCode'), flex: 1.4, minWidth: 120 },
      { field: 'name', headerName: t('pricingPlans.colName'), flex: 1.8, minWidth: 150 },
      {
        field: 'name_ar',
        headerName: t('pricingPlans.colNameAr'),
        flex: 1.4,
        minWidth: 120,
      },
      {
        field: 'menu_order',
        headerName: t('pricingPlans.colMenuOrder'),
        width: 100,
      },
      {
        field: 'show_in_menu',
        headerName: t('pricingPlans.colInMenu'),
        width: 120,
        renderCell: (params) =>
          params.value ? (
            <Chip size="sm" variant="soft" color="success">
              {t('actions.yes')}
            </Chip>
          ) : (
            <Chip size="sm" variant="soft" color="neutral">
              {t('actions.no')}
            </Chip>
          ),
      },
      {
        field: 'active',
        headerName: t('pricingPlans.colActive'),
        width: 90,
        valueGetter: (a, b) => {
          const row = valueGetterRow(a, b);
          return row?.active ? t('serviceManager.priceYes') : t('serviceManager.priceNo');
        },
      },
      {
        field: 'actions',
        headerName: t('trackingEvent.colActions'),
        flex: 1.6,
        minWidth: 140,
        sortable: false,
        renderCell: (params) => (
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <PermissionGate
              permissions={[PERMISSIONS.PRICING_PLANS_VIEW, PERMISSIONS.PRICING_PLANS_UPDATE]}
            >
              <Tooltip title={t('pricingPlans.tooltipOpen')}>
                <IconButton
                  size="sm"
                  variant="soft"
                  color="primary"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/PricingPlans/${params.row.id}`);
                  }}
                >
                  <Visibility fontSize="small" />
                </IconButton>
              </Tooltip>
            </PermissionGate>
            <PermissionGate permission={PERMISSIONS.PRICING_PLANS_UPDATE}>
              <Tooltip title={t('pricingPlans.tooltipEdit')}>
                <IconButton
                  size="sm"
                  variant="soft"
                  color="neutral"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/PricingPlans/${params.row.id}`);
                  }}
                >
                  <Edit fontSize="small" />
                </IconButton>
              </Tooltip>
            </PermissionGate>
            <PermissionGate permission={PERMISSIONS.PRICING_PLANS_DELETE}>
              <Tooltip title={t('pricingPlans.tooltipDelete')}>
                <IconButton
                  size="sm"
                  variant="soft"
                  color="danger"
                  onClick={(e) => {
                    e.stopPropagation();
                    openDeleteDialog(params.row);
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </PermissionGate>
          </Box>
        ),
      },
    ],
    [t, navigate]
  );

  const rowCount = data?.count != null ? data.count : results.length;

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <form onSubmit={handleSubmitFilter}>
        <Box sx={{ display: 'flex' }} m={3}>
          <Box width="100%">
            <Box>
              <Breadcrumbs separator={<KeyboardArrowRight />} aria-label="breadcrumbs">
                <Link color="neutral" href="/PricingPlans">
                  {t('nav.pricingPlans')}
                </Link>
                <Link color="neutral" href="/PricingPlans">
                  {t('pricingPlans.breadcrumbManager')}
                </Link>
              </Breadcrumbs>
              <Typography textAlign="start" level="h1">
                {t('pricingPlans.pageTitle')}
              </Typography>
              <Typography level="body-sm" sx={{ color: 'neutral.600', mt: 0.5 }}>
                {t('pricingPlans.pageHint')}
              </Typography>
            </Box>

            <PermissionGate permissions={[PERMISSIONS.PRICING_PLANS_LIST]}>
              <Grid container spacing={1} sx={{ flexGrow: 1, mb: 1, mt: 2 }}>
                <Grid xs={12}>
                  <Typography level="h3" textAlign="start">
                    {t('pricingPlans.filterHeading')}
                  </Typography>
                </Grid>
                <Grid xs={12} sm={6} md={4}>
                  <CustomTextField
                    label={t('pricingPlans.searchLabel')}
                    type="text"
                    value={searchField}
                    onChange={(e) => setSearchField(e.target.value)}
                    fullWidth
                  />
                </Grid>
                <Grid xs={12} sm={6} md={4}>
                  <CustomTextField
                    label={t('pricingPlans.pricingRuleSearchLabel')}
                    type="text"
                    value={pricingRuleSearch}
                    onChange={(e) => setPricingRuleSearch(e.target.value)}
                    fullWidth
                  />
                </Grid>
                <Grid xs={12} sm={6} md={3}>
                  <Typography level="body-xs" sx={{ mb: 0.5 }}>
                    {t('pricingPlans.activeFilter')}
                  </Typography>
                  <Select
                    value={activeFilter}
                    onChange={(_, v) => setActiveFilter(v || '')}
                    sx={{ width: '100%' }}
                  >
                    <Option value="">{t('pricingPlans.activeAny')}</Option>
                    <Option value="true">{t('serviceManager.priceYes')}</Option>
                    <Option value="false">{t('serviceManager.priceNo')}</Option>
                  </Select>
                </Grid>
                <Grid xs={12} sm={6} md={3}>
                  <Typography level="body-xs" sx={{ mb: 0.5 }}>
                    {t('pricingPlans.menuFilter')}
                  </Typography>
                  <Select
                    value={menuFilter}
                    onChange={(_, v) => setMenuFilter(v || '')}
                    sx={{ width: '100%' }}
                  >
                    <Option value="">{t('pricingPlans.menuAny')}</Option>
                    <Option value="true">{t('serviceManager.priceYes')}</Option>
                    <Option value="false">{t('serviceManager.priceNo')}</Option>
                  </Select>
                </Grid>
                <Grid xs={12} sm={6} md={3}>
                  <CustomTextField
                    label={t('pricingPlans.filterCode')}
                    type="text"
                    value={codeFilter}
                    onChange={(e) => setCodeFilter(e.target.value)}
                    fullWidth
                  />
                </Grid>
                <Grid xs={12} sm={6} md={3}>
                  <CustomTextField
                    label={t('pricingPlans.filterName')}
                    type="text"
                    value={nameFilter}
                    onChange={(e) => setNameFilter(e.target.value)}
                    fullWidth
                  />
                </Grid>
                <Grid xs={12} sm={6} md={3}>
                  <CustomTextField
                    label={t('pricingPlans.filterNameAr')}
                    type="text"
                    value={nameArFilter}
                    onChange={(e) => setNameArFilter(e.target.value)}
                    fullWidth
                  />
                </Grid>
                <Grid xs={12} sm={6} md={3}>
                  <CustomTextField
                    label={t('pricingPlans.filterDescription')}
                    type="text"
                    value={descriptionFilter}
                    onChange={(e) => setDescriptionFilter(e.target.value)}
                    fullWidth
                  />
                </Grid>
                <Grid xs={12} sm={6} md={3}>
                  <CustomTextField
                    label={t('pricingPlans.filterDescriptionAr')}
                    type="text"
                    value={descriptionArFilter}
                    onChange={(e) => setDescriptionArFilter(e.target.value)}
                    fullWidth
                  />
                </Grid>
                <Grid xs={12} sm={6} md={3}>
                  <CustomTextField
                    label={t('pricingPlans.filterMenuLabelEn')}
                    type="text"
                    value={menuLabelEnFilter}
                    onChange={(e) => setMenuLabelEnFilter(e.target.value)}
                    fullWidth
                  />
                </Grid>
                <Grid xs={12} sm={6} md={3}>
                  <CustomTextField
                    label={t('pricingPlans.filterMenuLabelAr')}
                    type="text"
                    value={menuLabelArFilter}
                    onChange={(e) => setMenuLabelArFilter(e.target.value)}
                    fullWidth
                  />
                </Grid>
                <Grid xs={12} sm={6} md={3}>
                  <CustomTextField
                    label={t('pricingPlans.filterMenuOrder')}
                    type="number"
                    value={menuOrderFilter}
                    onChange={(e) => setMenuOrderFilter(e.target.value)}
                    fullWidth
                  />
                </Grid>
                <Grid xs={12} sm={6} md={3}>
                  <CustomTextField
                    label={t('pricingPlans.filterCreatedFrom')}
                    type="date"
                    value={createdAfter}
                    onChange={(e) => setCreatedAfter(e.target.value)}
                    fullWidth
                  />
                </Grid>
                <Grid xs={12} sm={6} md={3}>
                  <CustomTextField
                    label={t('pricingPlans.filterCreatedTo')}
                    type="date"
                    value={createdBefore}
                    onChange={(e) => setCreatedBefore(e.target.value)}
                    fullWidth
                  />
                </Grid>
                <Grid xs={12} sm={6} md={3}>
                  <CustomTextField
                    label={t('pricingPlans.filterModifiedFrom')}
                    type="date"
                    value={modifiedAfter}
                    onChange={(e) => setModifiedAfter(e.target.value)}
                    fullWidth
                  />
                </Grid>
                <Grid xs={12} sm={6} md={3}>
                  <CustomTextField
                    label={t('pricingPlans.filterModifiedTo')}
                    type="date"
                    value={modifiedBefore}
                    onChange={(e) => setModifiedBefore(e.target.value)}
                    fullWidth
                  />
                </Grid>
              </Grid>
              <Button type="submit" loading={isLoading}>
                {t('actions.search')}
              </Button>
            </PermissionGate>

            <Grid mt={2} container spacing={2} justifyContent="flex-end">
              <PermissionGate permission={PERMISSIONS.PRICING_PLANS_DELETE}>
                <Grid>
                  <Tooltip
                    title={
                      selectionModel.length === 0
                        ? t('pricingPlans.tooltipBulkDeleteSelect')
                        : t('pricingPlans.tooltipBulkDelete')
                    }
                  >
                    <span>
                      <IconButton
                        onClick={openBulkDeleteDialog}
                        disabled={selectionModel.length === 0}
                        color="danger"
                        size="large"
                      >
                        <DeleteIcon sx={{ fontSize: 25 }} />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Grid>
              </PermissionGate>
              <PermissionGate permission={PERMISSIONS.PRICING_PLANS_CREATE}>
                <Grid>
                  <Tooltip title={t('pricingPlans.tooltipAdd')}>
                    <IconButton
                      onClick={() => navigate('/PricingPlans/Add')}
                      color="primary"
                      size="large"
                    >
                      <Add sx={{ fontSize: 25 }} />
                    </IconButton>
                  </Tooltip>
                </Grid>
              </PermissionGate>
            </Grid>

            <PermissionGate permissions={[PERMISSIONS.PRICING_PLANS_LIST]}>
              <Box sx={{ height: '70vh', width: '100%', mt: 2 }}>
                <DataGrid
                  slots={dataGridNativeCheckboxSlots}
                  rows={results}
                  columns={columns}
                  pagination
                  paginationMode="server"
                  rowCount={rowCount}
                  paginationModel={paginationModel}
                  pageSizeOptions={[25, 50, 100]}
                  onPaginationModelChange={setPaginationModel}
                  loading={isLoading}
                  checkboxSelection
                  disableRowSelectionOnClick
                  rowSelectionModel={selectionModel}
                  onRowSelectionModelChange={setSelectionModel}
                  hideFooterSelectedRowCount
                  onRowDoubleClick={(params) =>
                    navigate(`/PricingPlans/${params.row.id}`)
                  }
                />
              </Box>
            </PermissionGate>

            <DeleteAlert
              open={deleteAlert}
              onClose={() => setDeleteAlert(false)}
              onDelete={() => selectedRow && deleteRow(selectedRow)}
              loading={deleteLoading || deleteConfirmLoading}
              title={t('pricingPlans.deleteConfirmTitle')}
              body={deleteConfirmBody}
              confirmLabel={t('actions.delete')}
            />

            <DeleteAlert
              open={bulkDeleteAlert}
              onClose={() => setBulkDeleteAlert(false)}
              onDelete={bulkDeletePlans}
              loading={bulkDeleteLoading || bulkDeleteConfirmLoading}
              title={t('pricingPlans.bulkDeleteConfirmTitle', { planCount: selectionModel.length })}
              body={bulkDeleteConfirmBody}
              confirmLabel={t('actions.delete')}
            />

            <Snackbar
              open={successDelete}
              autoHideDuration={3000}
              onClose={() => {
                setSuccessDelete(false);
                setDeletedRuleCount(null);
                setBulkDeletedPlanCount(null);
                setBulkDeletedRuleCount(null);
              }}
              color="success"
              variant="soft"
            >
              {bulkDeleteSuccessMessage}
            </Snackbar>
            <Snackbar
              open={showCreatedSnack}
              autoHideDuration={3500}
              onClose={() => setShowCreatedSnack(false)}
              color="success"
              variant="soft"
            >
              {t('pricingPlans.snackbarCreated')}
            </Snackbar>
            <Snackbar
              open={failedDelete}
              autoHideDuration={6000}
              onClose={() => setFailedDelete(false)}
              color="danger"
              variant="soft"
            >
              {deleteErrorMsg || t('pricingPlans.deleteError')}
            </Snackbar>
          </Box>
        </Box>
      </form>
    </ThemeProvider>
  );
}
